// ResetPassword.js
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService, { errorHelpers, validationHelpers } from '../../utils/apiService';

const ResetPassword = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [resetComplete, setResetComplete] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
        setAlert(null);
    };

    const validateForm = () => {
        const newErrors = {};

        // Password validation
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else {
            const passwordValidation = validationHelpers.validatePassword(formData.password);
            if (!passwordValidation.isValid) {
                const errorMessages = Object.values(passwordValidation.errors).filter(Boolean);
                newErrors.password = errorMessages[0]; // Show first error
            }
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setAlert(null);

        try {
            const response = await apiService.auth.resetPassword(token, formData.password);

            setAlert({
                type: 'success',
                message: response.data.message
            });

            setResetComplete(true);

            // Redirect to home after delay
            setTimeout(() => {
                navigate('/');
            }, 3000);

        } catch (error) {
            const errorMessage = errorHelpers.getErrorMessage(error);
            const errorCode = errorHelpers.getErrorCode(error);

            if (errorCode === 'INVALID_RESET_TOKEN' || errorCode === 'EXPIRED_RESET_TOKEN') {
                setAlert({
                    type: 'error',
                    message: errorMessage + ' Please request a new password reset link.'
                });
            } else {
                setAlert({
                    type: 'error',
                    message: errorMessage
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoHome = () => {
        navigate('/');
    };

    if (resetComplete) {
        return (
            <div className="reset-password-page">
                <div className="reset-container">
                    <div className="reset-card">
                        <div className="reset-logo">
                            <div className="logo-icon">OU</div>
                            <h1>Olabisi Onabanjo University</h1>
                            <p>Certificate Verification System</p>
                        </div>

                        <div className="reset-content">
                            <div className="reset-success">
                                <div className="success-icon">✅</div>
                                <h2>Password Reset Successful!</h2>
                                <div className="success-message">
                                    Your password has been reset successfully. You can now log in with your new password.
                                </div>
                                <div className="success-actions">
                                    <button onClick={handleGoHome} className="btn btn-primary">
                                        Go to Login
                                    </button>
                                    <p className="redirect-text">
                                        Redirecting to home page in a few seconds...
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-password-page">
            <div className="reset-container">
                <div className="reset-card">
                    {/* Logo Section */}
                    <div className="reset-logo">
                        <div className="logo-icon">OU</div>
                        <h1>Olabisi Onabanjo University</h1>
                        <p>Certificate Verification System</p>
                    </div>

                    {/* Reset Form */}
                    <div className="reset-content">
                        <div className="reset-header">
                            <h2>Reset Your Password</h2>
                            <p>Enter your new password below to complete the reset process.</p>
                        </div>

                        {alert && (
                            <div className={`alert alert-${alert.type}`}>
                                {alert.message}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label" htmlFor="password">
                                    New Password
                                </label>
                                <div className="password-input-container">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        className={`form-input ${errors.password ? 'error' : ''}`}
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="Enter your new password"
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        disabled={loading}
                                    >
                                        {showPassword ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                {errors.password && (
                                    <div className="form-error">{errors.password}</div>
                                )}
                                <div className="password-requirements">
                                    <h4>Password Requirements:</h4>
                                    <ul>
                                        <li className={formData.password.length >= 8 ? 'valid' : ''}>
                                            At least 8 characters long
                                        </li>
                                        <li className={/[A-Z]/.test(formData.password) ? 'valid' : ''}>
                                            Contains uppercase letter
                                        </li>
                                        <li className={/[a-z]/.test(formData.password) ? 'valid' : ''}>
                                            Contains lowercase letter
                                        </li>
                                        <li className={/\d/.test(formData.password) ? 'valid' : ''}>
                                            Contains at least one number
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label" htmlFor="confirmPassword">
                                    Confirm New Password
                                </label>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Confirm your new password"
                                    disabled={loading}
                                />
                                {errors.confirmPassword && (
                                    <div className="form-error">{errors.confirmPassword}</div>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary btn-block"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="loading-spinner mr-2"></span>
                                        Resetting Password...
                                    </>
                                ) : (
                                    'Reset Password'
                                )}
                            </button>
                        </form>

                        <div className="reset-footer">
                            <button
                                onClick={handleGoHome}
                                className="link-button"
                                disabled={loading}
                            >
                                ← Back to Home
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Styles */}
            <style jsx>{`
        .reset-password-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .reset-container {
          width: 100%;
          max-width: 500px;
        }

        .reset-card {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .reset-logo {
          background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
          color: white;
          text-align: center;
          padding: 3rem 2rem 2rem;
        }

        .logo-icon {
          width: 60px;
          height: 60px;
          background: white;
          color: var(--primary-blue);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: bold;
          margin: 0 auto 1rem;
        }

        .reset-logo h1 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .reset-logo p {
          opacity: 0.9;
          margin: 0;
        }

        .reset-content {
          padding: 3rem 2rem;
        }

        .reset-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .reset-header h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 0.5rem;
        }

        .reset-header p {
          color: var(--gray-600);
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: var(--gray-700);
        }

        .password-input-container {
          position: relative;
        }

        .form-input {
          width: 100%;
          padding: 0.75rem;
          padding-right: 3rem;
          border: 2px solid var(--gray-200);
          border-radius: 0.5rem;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--secondary-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-input.error {
          border-color: var(--error-red);
        }

        .password-toggle {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.25rem;
          color: var(--gray-500);
        }

        .password-toggle:hover {
          color: var(--gray-700);
        }

        .form-error {
          color: var(--error-red);
          font-size: 0.875rem;
          margin-top: 0.25rem;
        }

        .password-requirements {
          margin-top: 1rem;
          background: var(--gray-50);
          padding: 1rem;
          border-radius: 0.5rem;
          border: 1px solid var(--gray-200);
        }

        .password-requirements h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--gray-700);
          margin-bottom: 0.5rem;
        }

        .password-requirements ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .password-requirements li {
          font-size: 0.875rem;
          color: var(--gray-600);
          margin-bottom: 0.25rem;
          display: flex;
          align-items: center;
        }

        .password-requirements li::before {
          content: '○';
          margin-right: 0.5rem;
          color: var(--gray-400);
        }

        .password-requirements li.valid {
          color: var(--success-green);
        }

        .password-requirements li.valid::before {
          content: '✓';
          color: var(--success-green);
        }

        .btn {
          background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 500;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-block {
          width: 100%;
        }

        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .mr-2 {
          margin-right: 0.5rem;
        }

        .reset-footer {
          text-align: center;
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--gray-200);
        }

        .link-button {
          background: none;
          border: none;
          color: var(--secondary-blue);
          cursor: pointer;
          font-size: 1rem;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .link-button:hover:not(:disabled) {
          color: var(--primary-blue);
          text-decoration: underline;
        }

        .link-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .alert {
          padding: 1rem;
          border-radius: 0.5rem;
          margin-bottom: 1.5rem;
          border: 1px solid transparent;
        }

        .alert-success {
          background: var(--green-50);
          color: var(--success-green);
          border-color: var(--green-200);
        }

        .alert-error {
          background: var(--red-50);
          color: var(--error-red);
          border-color: var(--red-200);
        }

        /* Success state styles */
        .reset-success {
          text-align: center;
        }

        .success-icon {
          font-size: 4rem;
          margin-bottom: 1.5rem;
        }

        .reset-success h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--gray-800);
          margin-bottom: 1rem;
        }

        .success-message {
          background: var(--green-50);
          color: var(--success-green);
          border: 1px solid var(--green-200);
          padding: 1rem;
          border-radius: 0.5rem;
          margin-bottom: 2rem;
        }

        .success-actions {
          margin-top: 2rem;
        }

        .redirect-text {
          font-size: 0.875rem;
          color: var(--gray-500);
          margin-top: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 640px) {
          .reset-password-page {
            padding: 1rem;
          }

          .reset-content {
            padding: 2rem 1.5rem;
          }

          .reset-logo {
            padding: 2rem 1.5rem 1.5rem;
          }

          .reset-logo h1 {
            font-size: 1.25rem;
          }

          .form-input {
            padding-right: 3rem;
          }
        }
      `}</style>
        </div>
    );
};

export default ResetPassword;